import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaArrowRight,
  FaBox,
  FaCheckCircle,
  FaChevronDown,
  FaChevronUp,
  FaFileAlt,
  FaHeart,
  FaInfoCircle,
  FaList,
  FaLock,
  FaMinus,
  FaPlus,
  FaRegTrashAlt,
  FaShieldAlt,
  FaShoppingCart,
  FaStar,
  FaTimes,
  FaTruck,
  FaUndo,
  FaUserShield,
} from "react-icons/fa";
import { toast } from "react-toastify";
import B2BHubHomeHeader from "../../components/B2BHubHomeHeader";
import { normalizeRecoProduct } from "../../components/marketplace/MarketplaceRelatedProductsSection";
import b2bHubHomeService from "../../services/b2bHubHome.service";
import HubHomeFooter from "../../components/hub/HubHomeFooter";
import HubFloatingRail from "../../components/hub/HubFloatingRail";
import { EMPTY_MARKETPLACE_CART, MARKETPLACE_CART_TRUST_ITEMS } from "../../constants/marketplaceCart";
import {
  addMarketplaceCartItem,
  fetchMarketplaceCart,
  generateMarketplaceCartRfqs,
  moveMarketplaceCartToWishlist,
  removeMarketplaceCartItem,
  removeSelectedMarketplaceCartItems,
  saveMarketplaceCartAsList,
  updateMarketplaceCartItem,
  updateMarketplaceCartSelection,
} from "../../services/marketplaceCart.service";
import StorageService from "../../services/StorageService";
import { resolvePublicFileUrl } from "../../constants/api-urls";
import { formatCartInr, normalizeMarketplaceCart, computeMarketplaceCartSummary } from "../../utils/marketplaceCart.util";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../assets/styles/home.scss";
import "../../assets/styles/my_cart.scss";
import "../../assets/styles/my_lists.scss";

function CartTrustIcon({ type }) {
  switch (type) {
    case "secure":
      return <FaShieldAlt aria-hidden />;
    case "trade":
      return <FaUserShield aria-hidden />;
    case "returns":
      return <FaUndo aria-hidden />;
    case "delivery":
      return <FaBox aria-hidden />;
    default:
      return <FaShieldAlt aria-hidden />;
  }
}

function ProductThumb({ name, imageUrl }) {
  if (imageUrl) {
    return (
      <div className="mycart-item__thumb mycart-item__thumb--image">
        <img src={resolvePublicFileUrl(imageUrl)} alt={name} />
      </div>
    );
  }

  return (
    <div className="mycart-item__thumb" aria-hidden>
      <FaBox />
      <span>{name.split(" ").slice(0, 2).join(" ")}</span>
    </div>
  );
}

export default function MyCartPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [cart, setCart] = useState(EMPTY_MARKETPLACE_CART);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(true);
  const [saveListOpen, setSaveListOpen] = useState(false);
  const [saveListName, setSaveListName] = useState("");
  const [pendingSaveItemIds, setPendingSaveItemIds] = useState(null);
  const [fallbackRelated, setFallbackRelated] = useState([]);

  const loadCart = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMarketplaceCart();
      setCart(normalizeMarketplaceCart(data) || EMPTY_MARKETPLACE_CART);
    } catch (err) {
      const message = err?.response?.data?.message || err?.message || "Could not load cart.";
      toast.error(message);
      setCart(EMPTY_MARKETPLACE_CART);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!StorageService.getToken()) {
      navigate("/login", { state: { from: location.pathname } });
      return undefined;
    }
    void loadCart();
    return undefined;
  }, [loadCart, location.pathname, navigate]);

  const totalItems = cart.totalItems || 0;
  const allItems = useMemo(
    () => cart.suppliers.flatMap((supplier) => supplier.items || []),
    [cart.suppliers]
  );

  const relatedProducts = useMemo(() => {
    const cartProductIds = new Set(allItems.map((item) => Number(item.productId)).filter(Boolean));
    const source = cart.frequentlyBoughtTogether?.length ? cart.frequentlyBoughtTogether : fallbackRelated;
    return source
      .map(normalizeRecoProduct)
      .filter((product) => product.id && !cartProductIds.has(product.id))
      .slice(0, 4);
  }, [cart.frequentlyBoughtTogether, fallbackRelated, allItems]);

  useEffect(() => {
    if (loading || !allItems.length || cart.frequentlyBoughtTogether?.length) {
      setFallbackRelated([]);
      return undefined;
    }

    let cancelled = false;

    (async () => {
      try {
        const res = await b2bHubHomeService.getMarketplace({ page: 1, limit: 12 });
        const products = res?.data?.products || res?.data?.data?.products || res?.products || [];
        if (!cancelled) setFallbackRelated(Array.isArray(products) ? products : []);
      } catch {
        if (!cancelled) setFallbackRelated([]);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [loading, cart.frequentlyBoughtTogether, allItems]);
  const allSelected = allItems.length > 0 && allItems.every((item) => item.isSelected);
  const selectedItems = useMemo(() => allItems.filter((item) => item.isSelected), [allItems]);
  const summary = useMemo(
    () => computeMarketplaceCartSummary(selectedItems),
    [selectedItems]
  );

  const runCartMutation = async (mutation) => {
    setSaving(true);
    try {
      const data = await mutation();
      setCart(normalizeMarketplaceCart(data) || EMPTY_MARKETPLACE_CART);
      return data;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Cart update failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const applyCartActionResult = (result) => {
    if (result?.cart) {
      setCart(normalizeMarketplaceCart(result.cart) || EMPTY_MARKETPLACE_CART);
      return true;
    }
    return false;
  };

  const runCartAction = async (mutation) => {
    setSaving(true);
    try {
      const result = await mutation();
      if (applyCartActionResult(result)) return result;
      toast.error("Cart update failed.");
      return null;
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Cart update failed.");
      return null;
    } finally {
      setSaving(false);
    }
  };

  const ensureSelectedItems = (itemIds = null) => {
    if (Array.isArray(itemIds) && itemIds.length) return itemIds;
    if (!selectedItems.length) {
      toast.warning("Select items to continue.");
      return null;
    }
    return selectedItems.map((item) => item.id);
  };

  const defaultListName = () => {
    const date = new Date().toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
    return `Cart list - ${date}`;
  };

  const openSaveListModal = (itemIds = null) => {
    const resolvedIds = ensureSelectedItems(itemIds);
    if (!resolvedIds) return;
    setPendingSaveItemIds(resolvedIds);
    setSaveListName(defaultListName());
    setSaveListOpen(true);
  };

  const closeSaveListModal = () => {
    setSaveListOpen(false);
    setSaveListName("");
    setPendingSaveItemIds(null);
  };

  const handleMoveToWishlist = async (itemIds = null) => {
    const resolvedIds = ensureSelectedItems(itemIds);
    if (!resolvedIds) return;
    const result = await runCartAction(() =>
      moveMarketplaceCartToWishlist({ itemIds: resolvedIds, onlySelected: false })
    );
    if (result) {
      toast.success(
        result.movedCount === 1
          ? "Item moved to wishlist."
          : `${result.movedCount} items moved to wishlist.`
      );
    }
  };

  const handleSaveAsList = async (event) => {
    event?.preventDefault();
    const name = saveListName.trim();
    if (!name) {
      toast.warning("Enter a list name.");
      return;
    }
    if (!pendingSaveItemIds?.length) {
      closeSaveListModal();
      return;
    }
    const result = await runCartAction(() =>
      saveMarketplaceCartAsList(name, { itemIds: pendingSaveItemIds, onlySelected: false })
    );
    if (result) {
      closeSaveListModal();
      toast.success(`Saved ${result.savedCount} item(s) to "${result.listName}".`);
    }
  };

  const handleGenerateRfq = async (itemIds = null) => {
    const resolvedIds = ensureSelectedItems(itemIds);
    if (!resolvedIds) return;
    const result = await runCartAction(() =>
      generateMarketplaceCartRfqs({ itemIds: resolvedIds, onlySelected: false })
    );
    if (!result) return;

    const createdCount = result.created?.length || 0;
    const skippedCount = result.skipped?.length || 0;

    if (createdCount) {
      toast.success(
        createdCount === 1
          ? "RFQ created from cart item."
          : `${createdCount} RFQs created from cart items.`
      );
      navigate("/buyer-rfq");
      return;
    }

    if (skippedCount) {
      toast.warning(result.skipped[0]?.reason || "Could not generate RFQ for selected items.");
    }
  };

  const updateQuantity = async (item, delta) => {
    const min = item.minOrder || 1;
    const nextQty = Math.max(min, item.quantity + delta);
    if (nextQty === item.quantity) return;
    await runCartMutation(() => updateMarketplaceCartItem(item.id, { quantity: nextQty }));
  };

  const removeItem = async (item) => {
    const data = await runCartMutation(() => removeMarketplaceCartItem(item.id));
    if (data) toast.info("Item removed from cart.");
  };

  const toggleItem = async (item) => {
    await runCartMutation(() =>
      updateMarketplaceCartItem(item.id, { isSelected: !item.isSelected })
    );
  };

  const toggleSupplier = async (supplier) => {
    const allOn = (supplier.items || []).every((item) => item.isSelected);
    await runCartMutation(() =>
      updateMarketplaceCartSelection({
        supplierId: supplier.id,
        selected: !allOn,
      })
    );
  };

  const toggleSelectAll = async () => {
    await runCartMutation(() =>
      updateMarketplaceCartSelection({
        selectAll: true,
        selected: !allSelected,
      })
    );
  };

  const handleBulkRemove = async () => {
    const hasSelected = allItems.some((item) => item.isSelected);
    if (!hasSelected) {
      toast.warning("Select items to remove.");
      return;
    }
    const data = await runCartMutation(() => removeSelectedMarketplaceCartItems());
    if (data) toast.info("Selected items removed.");
  };

  const handleAddFbt = async (product) => {
    const data = await runCartMutation(() => addMarketplaceCartItem(product.id, product.moq || 1));
    if (data) toast.success(`${product.title} added to cart.`);
  };

  const handleProceedToCheckout = () => {
    if (!selectedItems.length) {
      toast.warning("Select at least one item to checkout.");
      return;
    }
    navigate("/my-cart/checkout");
  };

  return (
    <div className="mycart-page">
      <B2BHubHomeHeader />

      <main className="mycart-main">
        <nav className="mycart-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span aria-hidden>›</span>
          <span aria-current="page">My Cart</span>
        </nav>

        <div className="mycart-head">
          <h1>My Cart ({totalItems} Items)</h1>
          <div className="mycart-head__aside">
            <nav className="mylist-nav mylist-nav--compact" aria-label="Shopping lists">
              <Link to="/my-wishlist" className="mylist-nav__link">
                <FaHeart aria-hidden /> My Wishlist
              </Link>
              <Link to="/my-saved-lists" className="mylist-nav__link">
                <FaList aria-hidden /> My Saved Lists
              </Link>
            </nav>
            <Link to="/marketplace/products" className="mycart-head__continue">
              <FaArrowLeft aria-hidden />
              Continue Shopping
            </Link>
          </div>
        </div>

        <div className="mycart-trust" aria-label="Shopping guarantees">
          {MARKETPLACE_CART_TRUST_ITEMS.map((item) => (
            <div key={item.id} className="mycart-trust__item">
              <span className="mycart-trust__icon" aria-hidden>
                <CartTrustIcon type={item.icon} />
              </span>
              <div className="mycart-trust__text">
                <p className="mycart-trust__title">{item.title}</p>
                <p className="mycart-trust__desc">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {loading ? (
          <p className="mycart-status">Loading your cart…</p>
        ) : cart.suppliers.length === 0 ? (
          <div className="mycart-empty">
            <h2>Your cart is empty</h2>
            <p>Browse products and add items to your cart to continue.</p>
            <Link to="/marketplace/products" className="mycart-summary__checkout">
              Browse Products
              <FaArrowRight aria-hidden />
            </Link>
          </div>
        ) : (
          <div className="mycart-layout">
            <section className="mycart-list" aria-label="Cart items">
              <div className="mycart-toolbar">
                <label className="mycart-toolbar__select">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    disabled={saving}
                    onChange={toggleSelectAll}
                  />
                  <span>Select All ({totalItems} items)</span>
                </label>
                <div className="mycart-toolbar__actions">
                  <button type="button" disabled={saving} onClick={handleBulkRemove}>
                    <FaRegTrashAlt aria-hidden /> Remove
                  </button>
                  <button type="button" disabled={saving} onClick={() => handleMoveToWishlist()}>
                    <FaHeart aria-hidden /> Move to Wishlist
                  </button>
                  <button type="button" disabled={saving} onClick={() => openSaveListModal()}>
                    <FaList aria-hidden /> Save as List
                  </button>
                  <button type="button" disabled={saving} onClick={() => handleGenerateRfq()}>
                    <FaFileAlt aria-hidden /> Generate RFQ
                  </button>
                </div>
              </div>

              {cart.suppliers.map((supplier) => {
                const supplierSelected =
                  (supplier.items || []).length > 0 &&
                  (supplier.items || []).every((item) => item.isSelected);

                return (
                  <article key={supplier.id} className="mycart-supplier">
                    <header className="mycart-supplier__head">
                      <label className="mycart-supplier__select">
                        <input
                          type="checkbox"
                          checked={supplierSelected}
                          disabled={saving}
                          onChange={() => toggleSupplier(supplier)}
                        />
                      </label>
                      <div className="mycart-supplier__meta">
                        <span className="mycart-supplier__label">Supplier:</span>
                        <strong>{supplier.name}</strong>
                        {supplier.verified ? (
                          <span className="mycart-supplier__verified">
                            <FaCheckCircle aria-hidden /> Verified
                          </span>
                        ) : null}
                        <span className="mycart-supplier__gstin">GSTIN: {supplier.gstin}</span>
                        <button
                          type="button"
                          className="mycart-supplier__rate"
                          disabled
                          title="Supplier rating is available after goods receipt is approved"
                        >
                          <FaStar aria-hidden /> Rate Supplier
                        </button>
                      </div>
                      <span className="mycart-supplier__delivery">
                        Est. Delivery: {supplier.estDelivery}
                      </span>
                    </header>

                    {(supplier.items || []).map((item) => {
                      const lineTotal = item.unitPrice * item.quantity;
                      return (
                        <div key={item.id} className="mycart-item">
                          <label className="mycart-item__check">
                            <input
                              type="checkbox"
                              checked={Boolean(item.isSelected)}
                              disabled={saving}
                              onChange={() => toggleItem(item)}
                            />
                          </label>

                          <ProductThumb name={item.name} imageUrl={item.imageUrl} />

                          <div className="mycart-item__body">
                            <h2 className="mycart-item__title">{item.name}</h2>
                            <p className="mycart-item__sku">SKU: {item.sku}</p>
                            <div className="mycart-item__badges">
                              <span
                                className={`mycart-item__stock${
                                  item.inStock ? " mycart-item__stock--in" : " mycart-item__stock--out"
                                }`}
                              >
                                {item.inStock ? "In Stock" : "Out of Stock"}
                              </span>
                              <span>MOQ: {item.moq}</span>
                              <span>Lead Time: {item.leadTime}</span>
                            </div>
                            <div className="mycart-item__links">
                              <button
                                type="button"
                                disabled={saving}
                                onClick={() => handleMoveToWishlist([item.id])}
                              >
                                Save for Later
                              </button>
                              <button type="button" disabled={saving} onClick={() => removeItem(item)}>
                                Remove
                              </button>
                            </div>
                          </div>

                          <div className="mycart-item__price">
                            <span className="mycart-item__unit">
                              {formatCartInr(item.unitPrice)} / {item.unit}
                            </span>
                          </div>

                          <div className="mycart-item__qty">
                            <div className="mycart-qty">
                              <button
                                type="button"
                                aria-label="Decrease quantity"
                                disabled={saving}
                                onClick={() => updateQuantity(item, -1)}
                              >
                                <FaMinus aria-hidden />
                              </button>
                              <span>{item.quantity}</span>
                              <button
                                type="button"
                                aria-label="Increase quantity"
                                disabled={saving}
                                onClick={() => updateQuantity(item, 1)}
                              >
                                <FaPlus aria-hidden />
                              </button>
                            </div>
                            <span className="mycart-item__min">Min. Order: {item.minOrder}</span>
                          </div>

                          <div className="mycart-item__total">
                            <strong>{formatCartInr(lineTotal)}</strong>
                            <button
                              type="button"
                              className="mycart-item__remove-icon"
                              aria-label={`Remove ${item.name}`}
                              disabled={saving}
                              onClick={() => removeItem(item)}
                            >
                              <FaTimes aria-hidden />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </article>
                );
              })}

              {summary.freeShippingGap > 0 ? (
                <div className="mycart-shipping-banner">
                  Add more products worth <strong>{formatCartInr(summary.freeShippingGap)}</strong> to get{" "}
                  <strong>FREE Shipping</strong>
                  <Link to="/marketplace/products">View eligible products</Link>
                </div>
              ) : null}

              {relatedProducts.length ? (
                <section className="mycart-fbt" aria-label="Related products">
                  <div className="mycart-fbt__head">
                    <h2>Frequently Bought Together</h2>
                    <Link to="/marketplace/products">View More</Link>
                  </div>
                  <div className="mycart-fbt__grid">
                    {relatedProducts.map((product) => (
                      <article key={product.id} className="mycart-fbt__card">
                        <Link
                          to={`/marketplace/products/${product.id}`}
                          className="mycart-fbt__media"
                          aria-label={`View ${product.title}`}
                        >
                          <div className="mycart-fbt__thumb">
                            {product.imageUrl ? (
                              <img src={resolvePublicFileUrl(product.imageUrl)} alt={product.title} />
                            ) : (
                              <FaBox />
                            )}
                          </div>
                        </Link>
                        <h3>
                          <Link to={`/marketplace/products/${product.id}`}>{product.title}</Link>
                        </h3>
                        <p className="mycart-fbt__unit">{product.unitLabel}</p>
                        <div className="mycart-fbt__rating" aria-label="4.8 out of 5 stars">
                          {[1, 2, 3, 4, 5].map((star) => (
                            <FaStar key={star} color={star <= 5 ? "#f97316" : "#e2e8f0"} aria-hidden />
                          ))}
                          <span>4.8</span>
                        </div>
                        <p className="mycart-fbt__price">{product.priceLabel}</p>
                        <button
                          type="button"
                          className="mycart-fbt__add"
                          disabled={saving}
                          onClick={() => handleAddFbt(product)}
                        >
                          <FaShoppingCart aria-hidden /> Add to Cart
                        </button>
                      </article>
                    ))}
                  </div>
                </section>
              ) : null}
            </section>

            <aside className="mycart-summary" aria-label="Order summary">
              <div className="mycart-summary__card">
                <button
                  type="button"
                  className="mycart-summary__toggle"
                  onClick={() => setSummaryOpen((v) => !v)}
                  aria-expanded={summaryOpen}
                >
                  <span>Order Summary</span>
                  {summaryOpen ? <FaChevronUp aria-hidden /> : <FaChevronDown aria-hidden />}
                </button>

                {summaryOpen ? (
                  <>
                    <dl className="mycart-summary__lines">
                      <div>
                        <dt>
                          Items ({summary.itemUnits}) <FaInfoCircle aria-hidden />
                        </dt>
                        <dd>{formatCartInr(summary.subtotal)}</dd>
                      </div>
                      <div>
                        <dt>Subtotal</dt>
                        <dd>{formatCartInr(summary.subtotal)}</dd>
                      </div>
                      <div>
                        <dt>
                          GST (18%) <FaInfoCircle aria-hidden />
                        </dt>
                        <dd>{formatCartInr(summary.gst)}</dd>
                      </div>
                      <div>
                        <dt>
                          Shipping Charges <FaInfoCircle aria-hidden />
                        </dt>
                        <dd>{formatCartInr(summary.shipping)}</dd>
                      </div>
                      <div>
                        <dt>
                          Platform Fee <FaInfoCircle aria-hidden />
                        </dt>
                        <dd>{formatCartInr(summary.platformFee)}</dd>
                      </div>
                      {summary.discount > 0 ? (
                        <div className="mycart-summary__discount">
                          <dt>Discount</dt>
                          <dd>- {formatCartInr(summary.discount)}</dd>
                        </div>
                      ) : null}
                    </dl>

                    <div className="mycart-summary__grand">
                      <span>Grand Total</span>
                      <strong>{formatCartInr(summary.grandTotal)}</strong>
                    </div>

                    {summary.discount > 0 ? (
                      <p className="mycart-summary__save">
                        You will save {formatCartInr(summary.discount)} on this order
                      </p>
                    ) : null}

                    <div className="mycart-summary__delivery">
                      <FaTruck aria-hidden />
                      <div>
                        <span>Estimated Delivery</span>
                        <strong>{cart.estimatedDelivery || "—"}</strong>
                      </div>
                    </div>

                    <button
                      type="button"
                      className="mycart-summary__checkout"
                      disabled={saving || !selectedItems.length}
                      onClick={handleProceedToCheckout}
                    >
                      Proceed to Checkout
                      <FaArrowRight aria-hidden />
                    </button>
                    <button
                      type="button"
                      className="mycart-summary__secondary"
                      disabled={saving}
                      onClick={() => openSaveListModal()}
                    >
                      <FaFileAlt aria-hidden /> Save as Draft
                    </button>
                    <button
                      type="button"
                      className="mycart-summary__secondary"
                      disabled={saving}
                      onClick={() => handleGenerateRfq()}
                    >
                      <FaFileAlt aria-hidden /> Generate RFQ
                    </button>

                    <div className="mycart-summary__payments">
                      <span>We accept</span>
                      <div className="mycart-summary__payment-icons" aria-hidden>
                        <span>VISA</span>
                        <span>MC</span>
                        <span>UPI</span>
                        <span>RuPay</span>
                        <span>Net Banking</span>
                      </div>
                      <p>
                        <FaLock aria-hidden /> SSL Secured Payment
                      </p>
                    </div>
                  </>
                ) : null}
              </div>
            </aside>
          </div>
        )}
      </main>

      <HubHomeFooter />
      <HubFloatingRail />

      {saveListOpen ? (
        <div className="mycart-modal" role="dialog" aria-modal="true" aria-labelledby="mycart-save-list-title">
          <button
            type="button"
            className="mycart-modal__backdrop"
            aria-label="Close save list dialog"
            onClick={closeSaveListModal}
          />
          <div className="mycart-modal__panel">
            <div className="mycart-modal__head">
              <h2 id="mycart-save-list-title">Save as List</h2>
              <button type="button" className="mycart-modal__close" onClick={closeSaveListModal} aria-label="Close">
                <FaTimes aria-hidden />
              </button>
            </div>
            <form className="mycart-modal__body" onSubmit={handleSaveAsList}>
              <label htmlFor="mycart-save-list-name">List name</label>
              <input
                id="mycart-save-list-name"
                type="text"
                value={saveListName}
                onChange={(event) => setSaveListName(event.target.value)}
                placeholder="Enter list name"
                maxLength={255}
                autoFocus
                disabled={saving}
              />
              <p className="mycart-modal__hint">
                {pendingSaveItemIds?.length || 0} selected item(s) will be saved to this list.
              </p>
              <div className="mycart-modal__actions">
                <button type="button" className="mycart-modal__cancel" onClick={closeSaveListModal} disabled={saving}>
                  Cancel
                </button>
                <button type="submit" className="mycart-modal__submit" disabled={saving}>
                  Save List
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}
    </div>
  );
}
